import React, { useMemo, useState } from "react";

import { type AttributionFilter } from "@doitintl/cmp-models";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";

import { useCloudAnalyticsTransforms } from "../hooks/cloudAnalytics/useCloudAnalyticsTransforms";
import TooltipWithMaxWidth from "../Tooltip/TooltipWithMaxWidth";

type Props = {
  filter: AttributionFilter;
  nullFallback: string | null;
  title: string;
  ruleFilterWidth: number;
  maxChips?: number;
};

export default function AllocationRuleChips({ filter, nullFallback, maxChips = 2, title, ruleFilterWidth }: Props) {
  const [showDialog, setShowDialog] = useState(false);
  const filteredValues = useMemo(
    () => filter.values?.filter((v) => v && v !== nullFallback) || [],
    [filter.values, nullFallback]
  );
  const showMaxChip = filteredValues.length > maxChips;
  const transforms = useCloudAnalyticsTransforms();

  const toggleDialog = () => {
    setShowDialog((prev) => !prev);
  };

  const maxWidth = useMemo(() => {
    if (filteredValues.length === 1) {
      return 420 - ruleFilterWidth * 7;
    } else {
      return 200 - ruleFilterWidth * 4.5;
    }
  }, [filteredValues.length, ruleFilterWidth]);

  const getTransformValue = (value: string) => transforms?.[filter.id]?.(value, nullFallback) ?? value;

  return (
    <>
      {filteredValues.length > 0 && (
        <>
          {filteredValues.slice(0, maxChips).map((value) => {
            const label = getTransformValue(value);
            return (
              <TooltipWithMaxWidth key={value} title={label}>
                <span>
                  <Chip label={label} size="small" color="primary" sx={{ maxWidth }} variant="outlined" />
                </span>
              </TooltipWithMaxWidth>
            );
          })}

          {showMaxChip && (
            <Typography
              variant="caption"
              fontSize={12}
              color="primary"
              sx={{ textDecoration: "underline", cursor: "pointer" }}
              onClick={toggleDialog}
            >{`+${filteredValues.length - maxChips}`}</Typography>
          )}
        </>
      )}

      <Dialog open={showDialog} onClose={toggleDialog} fullWidth maxWidth="sm">
        <DialogTitle>{title} Properties</DialogTitle>
        <DialogContent dividers={true}>
          <Typography variant="body2">
            This dimension consists of the following {filteredValues.length} properties:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2, height: 250, overflow: "auto" }}>
            {filteredValues.map((value) => (
              <li key={value}>
                <Typography variant="body2">{getTransformValue(value)}</Typography>
              </li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
